import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '../../../components/ui/Tooltip';

interface Props {
  /** The value to display. null/undefined/'' render the placeholder. */
  value?: string | number | null;
  /** Text shown when the value is empty. Defaults to an em dash. */
  placeholder?: string;
  /** Fixed width in pixels — mirrors an input's `width=` for column alignment. */
  width?: number;
  className?: string;
  /**
   * Truncate the value to a single line with an ellipsis when it overflows its
   * column, revealing the full text in a hover tooltip. Opt-in (default off) so
   * existing callers that rely on natural wrapping/full display are unchanged.
   * Used for long values like email addresses that otherwise spill into the
   * neighbouring field.
   */
  truncate?: boolean;
  /** Names the value for assistive tech (and tests) when the visible `Label`
   *  beside it isn't programmatically associated — e.g. a computed result. */
  ariaLabel?: string;
}

/**
 * Read-only display of a quote field as plain text. Replaces the
 * disabled-looking `<TextInput>` / `<SelectInput>` previously used for
 * non-editable fields so they no longer masquerade as editable controls.
 *
 * Spacing mirrors the orderform inputs (`px-1.5 py-[3px]`, h-6, 12.5px text)
 * so it sits cleanly in the existing `Row`/`Label` layout. When a `width` is
 * given it is `flex-none` (matching a fixed-width input); otherwise it flexes
 * to fill the row like a flex-1 input.
 */
const FieldValue: React.FC<Props> = ({ value, placeholder = '—', width, className, truncate, ariaLabel }) => {
  const isEmpty = value === null || value === undefined || value === '';
  const text = isEmpty ? placeholder : String(value);
  const flexCls = width ? 'flex-none' : 'flex-1';

  // Only surface the hover tooltip when the value is actually being clipped, so
  // short (fully-visible) values don't sprout a redundant tooltip. Re-measured
  // on mount, when the text changes, and on viewport resize.
  const textRef = useRef<HTMLSpanElement>(null);
  const [isClipped, setIsClipped] = useState(false);
  useEffect(() => {
    if (!truncate) return;
    const measure = () => {
      const el = textRef.current;
      if (el) setIsClipped(el.scrollWidth > el.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [truncate, text]);

  const span = (
    <span
      aria-label={ariaLabel}
      className={[
        'min-w-0 inline-flex items-center h-6 px-1.5 py-[3px] text-[0.78125rem]',
        isEmpty ? 'text-slate-400 dark:text-slate-500' : 'text-black dark:text-slate-100',
        flexCls,
        className ?? '',
      ].join(' ')}
      style={width ? { width } : undefined}
    >
      {truncate ? <span ref={textRef} className="truncate flex-1 min-w-0">{text}</span> : text}
    </span>
  );

  if (truncate && !isEmpty) {
    return <Tooltip content={text} placement="top-start" disabled={!isClipped}>{span}</Tooltip>;
  }
  return span;
};

export default FieldValue;
